export const isDarkMode = (): boolean => document.documentElement.getAttribute('data-theme')?.includes('dark') ?? false;

export const textColor = (bgColor: string, luminance: number = 0.5): string => {
  const color = isHexColor(bgColor) ? bgColor.replace('#', '') : rgbFromColorName(bgColor);

  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  const colorLuminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  if (isDarkMode()) return colorLuminance > luminance ? 'black' : 'text-primary';
  return colorLuminance > luminance ? 'text-primary' : 'white';
}

export const opacityColor = (color: string, opacity: number = 0.5): string => {
  let c = isHexColor(color) ? color.replace('#', '') : rgbFromColorName(color);

  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const isHexColor = (color: string): boolean => {
  return /^#?[0-9A-Fa-f]{6}$/.test(color);
}

const rgbFromColorName = (colorName: string): string => {
  const tempElement = document.createElement('div');
  tempElement.style.color = colorName;
  document.body.appendChild(tempElement);

  const rgb = getComputedStyle(tempElement).color;
  document.body.removeChild(tempElement);

  const rgbValues = rgb.match(/\d+/g);
  if (rgbValues) {
    const r = parseInt(rgbValues[0]).toString(16).padStart(2, '0');
    const g = parseInt(rgbValues[1]).toString(16).padStart(2, '0');
    const b = parseInt(rgbValues[2]).toString(16).padStart(2, '0');
    return `${r}${g}${b}`;
  }

  return '000000';
}

export const saturateColor = (color: string, amount: number = 0.3) => {
  const colorName = isHexColor(color) ? color : rgbFromColorName(color);
  const [r, g, b] = hexToRgb(colorName);
  const hsl = rgbToHsl(r, g, b);
  hsl[1] = Math.min(1, hsl[1] + amount); // Aumenta a saturação
  const [newR, newG, newB] = hslToRgb(hsl[0], hsl[1], hsl[2]);
  return rgbToHex(newR, newG, newB);
}

export const typeToColor = (type?: string): string => {
  switch (type) {
    case 'primary': return 'primary';
    case 'default': return 'base-icon';
    case 'success': return 'success';
    case 'info': return 'info';
    case 'warning': return 'secondary';
    case 'error': return 'error';
    case 'empty': return 'empty';
    case 'black': return 'black';
    default: return 'base-icon';
  }
}

export const isColorName = (color: string): boolean => {
  const s = new Option().style;
  s.color = color;
  return s.color !== '';
}

export const resolvedColor = (color: string) => {
  if (color && !color.startsWith('#') && !color.startsWith('var(') && !isColorName(color)) {
    return `var(--${color})`;
  }

  return color.replace(/##/g, '#');
}

// UTILS

function hexToRgb(hex: string): [number, number, number] {
  const bigint = parseInt(hex.slice(1), 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  }
  else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export const colorBrightness = (color: string): number => {
  const colorName = isHexColor(color) ? color : rgbFromColorName(color);

  const [r, g, b] = hexToRgb(colorName);
  return (r * 299 + g * 587 + b * 114) / 1000;
}

export const increaseBrightness = (color: string, amount: number = 0.1): string => {
  let colorName = isHexColor(color) ? color : rgbFromColorName(color);
  if (!colorName.startsWith('#')) colorName = `#${colorName}`;

  const [r, g, b] = hexToRgb(colorName);
  const hsl = rgbToHsl(r, g, b);
  hsl[2] = Math.min(1, hsl[2] + amount); // Aumenta a luminosidade
  const [newR, newG, newB] = hslToRgb(hsl[0], hsl[1], hsl[2]);
  return rgbToHex(newR, newG, newB);
}

export const decreaseBrightness = (color: string, amount: number = 0.1): string => {
  let colorName = isHexColor(color) ? color : rgbFromColorName(color);
  if (!colorName.startsWith('#')) colorName = `#${colorName}`;

  const [r, g, b] = hexToRgb(colorName);
  const hsl = rgbToHsl(r, g, b);
  hsl[2] = Math.max(0, hsl[2] - amount); // Diminui a luminosidade
  const [newR, newG, newB] = hslToRgb(hsl[0], hsl[1], hsl[2]);
  return rgbToHex(newR, newG, newB);
}

export const parseBrightness = (color: string): string => {
  const brightness = colorBrightness(color);

  if (brightness > 192) {
    return decreaseBrightness(color, 0.1);
  }
  else if (brightness < 64) {
    return increaseBrightness(color, 0.1);
  }
  return color;
}

export const diffColors = (color1: string, color2: string): number => {
  const c1 = isHexColor(color1) ? color1 : rgbFromColorName(color1);
  const c2 = isHexColor(color2) ? color2 : rgbFromColorName(color2);

  const [r1, g1, b1] = hexToRgb(c1);
  const [r2, g2, b2] = hexToRgb(c2);

  return Math.abs(r1 - r2) + Math.abs(g1 - g2) + Math.abs(b1 - b2);
}